import React from 'react';
import PropTypes from 'prop-types';
import Navbar from './Navbar';

import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/Navbar/Navbar',
  component: Navbar,
  parameters: {
    layout: 'fullscreen',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Navbar` component provides a fixed, responsive navigation menu for web applications. It supports expandable and collapsible states, hierarchical menu items, and interactive submenus.\n\n',
      // When & how to use it?
      '\nUse the `Navbar` component to allow users to navigate between different pages or sections of an application. It is especially suitable for dashboards or admin interfaces with complex navigation requirements.\n\n',
      // Current Usage
      `\nThe \`Navbar\` component is used in the main layout of applications to provide a consistent navigation experience. It supports:
      \n- Expandable menus for better visibility of navigation options.
      \n- Collapsible mode for saving screen space.
      \n- Dynamic linking and hierarchical structure.\n\n`,
      // Related Components
      `\n- **[MenuItem](http://localhost:6006/?path=/docs/components-navbar-menuitem--docs)**: Used to render individual navigation links.
       \n- **[ExpandableMenuItem](http://localhost:6006/?path=/docs/components-navbar-expandablemenuitem--docs)**: Handles nested menu items for hierarchical navigation.\n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    organizationId: {
      control: { type: 'number' },
      description: 'The ID of the organization used for dynamically generating navigation links.\n\n',
      table: {
        type: { summary: 'number' },
        required: true,
      },
    },
    isDefaultExpand: {
      control: { type: 'boolean' },
      description: 'Determines whether the navbar is expanded by default.\n\n',
      table: {
        type: { summary: 'boolean' },
        required: false,
        defaultValue: { summary: 'false' },
      },
    },
  },
};

const NavbarTemplate = ({ organizationId, isDefaultExpand = false }) => {
  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        height: '100vh',
      }}
    >
      <Navbar organizationId={organizationId} isDefaultExpand={isDefaultExpand} />
      {/* A mock content area to simulate the application layout */}
      <div
        style={{
          flex: 1,
          backgroundColor: '#f5f5f5',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: '18px',
        }}
      >
        Application Content Area
      </div>
    </div>
  );
};

NavbarTemplate.propTypes = {
  organizationId: PropTypes.number.isRequired,
  isDefaultExpand: PropTypes.bool,
};

const Template = NavbarTemplate.bind({});

export const DefaultNavbar = Template.bind({});
DefaultNavbar.args = {
  organizationId: 1,
  isDefaultExpand: false,
};
DefaultNavbar.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant demonstrates the default collapsed state of the `Navbar` component. It is used in applications where the navbar should save screen space by default.\n\n',
  // Where is it currently used on the product?
  '\nThe `Navbar` component is used in the main layout of the application for navigation purposes.\n\n',
);

export const ExpandedNavbar = Template.bind({});
ExpandedNavbar.args = {
  organizationId: 1,
  isDefaultExpand: true,
};
ExpandedNavbar.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant demonstrates the expanded state of the `Navbar` component. It is useful for applications where users need quick access to all navigation items by default.\n\n',
  // Where is it currently used on the product?
  '\nThe `Navbar` component is used in the main layout of the application for navigation purposes.\n\n',
);
